import React, { memo } from 'react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import DivesIcon from 'components/icons/dives';
import PoolDivesIcon from 'components/icons/pool-dives';
import { Reference } from 'types/reference';
import Image, { ImageProps } from 'frontastic/lib/image';
import RatingIcon from '../rating';

export interface CardProps {
  image: ImageProps;
  title: string;
  description: string; // markdown content
  descriptionMinHeight?: number;
  eLearningRequired: boolean;
  ctaButtonText: string;
  ctaButtonUrl: Reference;
  ctaButtonDataAnalytic?: string;
  takeCourseTitle: string;
  takeCourseDetails: { description: string }[];
  takeCourseDetailsMinHeight?: number;
  whatToExpectTitle: string;
  confinedWaterDivesText: string;
  openWaterDivesText: string;
  whatToExpectMinHeight?: number;
  afterCourseTitle: string;
  afterCourseDetails: string;
  afterCourseDetailsMinHeight?: number;
  rating: number;
  ratingDescription: string;
  secondCtaButtonText?: string;
  secondCtaButtonUrl?: Reference;
  secondCtaButtonDataAnalytic?: string;
}

const Card: React.FC<CardProps> = ({
  title,
  description,
  descriptionMinHeight,
  image,
  eLearningRequired,
  ctaButtonText,
  ctaButtonUrl,
  ctaButtonDataAnalytic,
  takeCourseTitle,
  takeCourseDetails,
  takeCourseDetailsMinHeight,
  whatToExpectTitle,
  confinedWaterDivesText,
  openWaterDivesText,
  whatToExpectMinHeight,
  afterCourseTitle,
  afterCourseDetails,
  afterCourseDetailsMinHeight,
  rating,
  ratingDescription,
  secondCtaButtonText,
  secondCtaButtonUrl,
  secondCtaButtonDataAnalytic,
}) => {
  return (
    <div className="flex-1 bg-white shadow-md">
      <Image {...image} className="p-16 pb-0" />
      <div className="p-40">
        <RatingIcon rating={rating} ratingDescription={ratingDescription} className="mb-16" />
        <h4 className="text-padi-text-darkest text-16 font-bold md:text-20">{title}</h4>
        <div style={{ minHeight: `${descriptionMinHeight}px` }}>
          <Markdown
            markdown={description}
            className="prose text-14 text-padi-gray-darker marker:text-padi-gray-darker prose-strong:text-padi-gray-darkest prose-ul:list-disc prose-li:ml-4"
          />
        </div>
        <div className="pt-20 text-padi-blue-darkest" style={{ minHeight: `${takeCourseDetailsMinHeight}px` }}>
          <h6 className="text-16 text-padi-gray-darkest">{takeCourseTitle}</h6>
          {takeCourseDetails.map((item, index) => (
            <div className="flex flex-row gap-16 pt-14" key={index}>
              <span className="size-24">
                <CheckCircleIcon className="size-24 text-padi-blue" />
              </span>
              <p className="text-14">{item.description}</p>
            </div>
          ))}
        </div>
        <div className="pt-20 text-14 text-padi-blue-darkest" style={{ minHeight: `${whatToExpectMinHeight}px` }}>
          <h6 className="text-16 text-padi-gray-darkest">{whatToExpectTitle}</h6>
          {eLearningRequired && (
            <div className="flex flex-row gap-16 pt-16">
              <span className="size-24">
                <BookOpenIcon className="size-24" />
              </span>
              <p className="text-14">PADI eLearning</p>
            </div>
          )}
          {confinedWaterDivesText && (
            <div className="flex flex-row gap-16 pt-14">
              <span className="size-24">
                <PoolDivesIcon className="size-24 text-padi-blue" />
              </span>
              <p className="text-14">{confinedWaterDivesText}</p>
            </div>
          )}
          {openWaterDivesText && (
            <div className="flex flex-row gap-16 pt-14">
              <DivesIcon className="size-24 text-padi-blue" stroke="#172554" />
              <p className="text-14">{openWaterDivesText}</p>
            </div>
          )}
        </div>
        <div className="pt-20 text-padi-blue-darkest" style={{ minHeight: `${afterCourseDetailsMinHeight}px` }}>
          <h6 className="text-16 text-padi-gray-darkest">{afterCourseTitle}</h6>
          <Markdown
            markdown={afterCourseDetails}
            className="prose text-14 text-padi-gray-darkest marker:text-padi-gray-darkest prose-strong:text-padi-gray-darkest prose-ul:list-disc prose-li:ml-4"
          />
        </div>
        <div className="flex flex-row flex-wrap gap-12 pt-24">
          <Link
            padi-analytics={ctaButtonDataAnalytic}
            link={ctaButtonUrl}
            className={secondCtaButtonText ? 'w-full md:w-auto' : 'w-full'}
          >
            <Button variant="primary" size="full" className="bg-padi-blue px-20 py-12 text-16">
              {ctaButtonText}
            </Button>
          </Link>
          {secondCtaButtonText && secondCtaButtonUrl && (
            <Link padi-analytics={secondCtaButtonDataAnalytic} link={secondCtaButtonUrl} className="w-full md:w-auto">
              <Button
                variant="ghost"
                size="full"
                className="rounded-md border border-gray-200 bg-white px-20 py-12 text-16 text-padi-blue"
              >
                {secondCtaButtonText}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(Card);
